import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  IconButton,
  Snackbar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { gql, useMutation } from '@apollo/client';

interface EditEoltReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  eolt: any;
  refetch: () => void;
}

const EDIT_EOLT_REPORT = gql`
  mutation EditEoltReport($vin: String!, $imei: String!, $engineNumber: String!) {
    editEoltReport(vin: $vin, imei: $imei, engine_number: $engineNumber) {
      message
      status
    }
  }
`;

const EditEoltReportDialog: React.FC<EditEoltReportDialogProps> = ({ open, onClose, onSave, eolt, refetch }) => {
  const [form, setForm] = useState(eolt || {});
  const [originalValues, setOriginalValues] = useState(eolt || {});
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [imeiError, setImeiError] = useState<string | null>(null);
  const [vinError, setVinError] = useState<string | null>(null);

  useEffect(() => {
    setForm(eolt || {});
    setOriginalValues(eolt || {});
  }, [eolt]);

  const [editEoltReport, { loading }] = useMutation(EDIT_EOLT_REPORT, {
    onCompleted: (data) => {
      const { message, status } = data.editEoltReport;

      // Check if values have actually changed
      const hasChanges = form.vin !== originalValues.vin || form.imei !== originalValues.imei || form.engineNumber !== originalValues.engineNumber;

      if (hasChanges) {
        setSnackbar({ message, severity: 'success' });
        if (data.editEoltReport.message === 'Eolt report updated successfully.') {
          refetch();
          onSave();
        }
      } else {
        setSnackbar({ message: 'No changes were made.', severity: 'info' });
      }
    },
    onError: (err) => {
      console.error('Error editing eolt report:')
      setSnackbar({ message: err.message, severity: 'error' });
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Remove all whitespace from input value
    const trimmedValue = value.replace(/[^a-zA-Z0-9]/g, '');
    setForm({ ...form, [name]: trimmedValue });

    // Validate IMEI length when changed
    if (name === 'imei') {
      if (trimmedValue && trimmedValue.length !== 15) {
        setImeiError('IMEI must be 15 characters');
      } else {
        setImeiError(null);
      }
    }

    // Validate VIN length when changed
    if (name === 'vin') {
      if (trimmedValue && trimmedValue.length !== 17) {
        setVinError('VIN must be 17 characters');
      } else {
        setVinError(null);
      }
    }
  };

  const handleSave = () => {
    // Validate VIN length before saving
    if (form.vin && form.vin.length !== 17) {
      setSnackbar({ message: 'VIN must be 17 characters', severity: 'error' });
      return;
    }

    // Validate IMEI length before saving
    if (form.imei && form.imei.length !== 15) {
      setSnackbar({ message: 'IMEI must be 15 characters', severity: 'error' });
      return;
    }

    if (form.vin && form.imei && form.engineNumber) {
      editEoltReport({ variables: { vin: form.vin, imei: form.imei, engineNumber: form.engineNumber } });
    } else {
      setSnackbar({ message: 'VIN, IMEI and Engine Number are required.', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar(null);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '95vw', sm: 500, md: 600 },
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          Edit IMEI
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{ ml: 1, color: 'inherit', position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid sx={{ xs:12 , sm:6}}>
              <TextField
                label="VIN"
                name="vin"
                disabled
                value={form.vin || ''}
                onChange={handleChange}
                fullWidth
                margin="dense"
                required
                error={!!vinError}
                helperText={vinError}
                inputProps={{
                  maxLength: 17,
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  }
                }}
              />
              <TextField
                label="Engine Number"
                name="engineNumber"
                disabled
                value={form.engineNumber || ''}
                onChange={handleChange}
                fullWidth
                margin="dense"
                required
                inputProps={{
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  }
                }}
              />
              <TextField
                label="IMEI"
                name="imei"
                value={form.imei || ''}
                onChange={handleChange}
                fullWidth
                margin="dense"
                required
                error={!!imeiError}
                helperText={imeiError}
                inputProps={{
                  maxLength: 15,
                  onInput: (e: any) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="error"
            disabled={loading || !!imeiError || !!vinError}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar?.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        ContentProps={{
          sx: {
            backgroundColor: snackbar?.severity === 'success' ? 'green' :
              snackbar?.severity === 'error' ? 'red' : 'info',
            color: '#fff',
          },
        }}
      />
    </>
  );
};

export default EditEoltReportDialog;